import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from '@xstate/store/react';
import type { EntityNodeProps } from '../EntityKindHandler';
import { getAdrTriggerData, ADR_TRIGGER_SIZE } from '../../entityTypes';
import { useAdrById } from '../../../stores';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useCanvasStores } from '../../stores/CanvasContext';

// ============================================
// Helper: darken a hex color by a given amount (0–255)
// ============================================

function darkenHex(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  const num = parseInt(c.length === 3 ? c.split('').map(ch => ch + ch).join('') : c, 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ============================================
// Single ADR title tooltip helper
// ============================================

function AdrTitle({ adrId }: { adrId: string }) {
  const adr = useAdrById(adrId);
  return <>{adr?.title || adrId}</>;
}

// ============================================
// ADR Trigger Node (Red Dot)
// ============================================

export const AdrTriggerNode = memo(function AdrTriggerNode({ entity }: EntityNodeProps) {
  const data = getAdrTriggerData(entity);
  const count = data.adrIds.length;
  const dotColor = data.color || '#ef4444';
  const borderColor = darkenHex(dotColor, 30);
  const lockedLayerIds = useLockedLayerIds();

  const { uiStore } = useCanvasStores();

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isLocked = lockedLayerIds.has(entity.layerId);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Multi-ADR toggle: open dropdown when clicking a trigger with multiple ADRs.
  // Single-ADR click is handled by AdrTriggerHandler.onEntityClick via the delegation hook.
  const handleClick = useCallback((_e: React.MouseEvent) => {
    if (count > 1) {
      setMenuOpen(prev => !prev);
    }
  }, [count]);

  const handleSelectAdr = useCallback((adrId: string) => {
    uiStore.send({ type: 'adrDetail.open', adrId });
    setMenuOpen(false);
  }, [uiStore]);

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <>
      <div
        className="adr-trigger-dot"
        data-canvas-entity
        data-canvas-adr-trigger={entity.id}
        data-entity-id={entity.id}
        data-entity-kind={entity.kind}
        onClick={handleClick}
        style={{
          position: 'absolute',
          left: entity.x,
          top: entity.y,
          width: ADR_TRIGGER_SIZE,
          height: ADR_TRIGGER_SIZE,
          borderRadius: '50%',
          backgroundColor: dotColor,
          border: `2px solid ${borderColor}`,
          cursor: isLocked ? 'not-allowed' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isSelected ? '0 0 0 2px #60a5fa' : '0 2px 4px rgba(0,0,0,0.2)',
          userSelect: 'none',
        }}
        title={data.adrIds.length === 1 ? undefined : `${count} ADRs`}
      >
        {count > 1 && (
          <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
            {count}
          </span>
        )}

        {/* Context menu for multiple ADRs */}
        {menuOpen && (
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: ADR_TRIGGER_SIZE + 4,
              left: 0,
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: 160,
              zIndex: 1000,
              padding: 4,
            }}
          >
            {data.adrIds.map(id => (
              <button
                key={id}
                onClick={(e) => { e.stopPropagation(); handleSelectAdr(id); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 10,
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: 3,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  color: '#374151',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <AdrTitle adrId={id} />
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
});
